/**
 * 보안 파일 저장소 Context
 * 암호화된 파일 관리 상태 및 로직
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { usePin } from './PinContext';
import {
  uploadSecureFile,
  downloadSecureFile,
  getSecureFiles,
  updateFileMetadata,
  deleteSecureFile,
  createShareLink
} from '../services/secureFilesService';

const SecureFilesContext = createContext({});

export function useSecureFiles() {
  const context = useContext(SecureFilesContext);
  if (!context) {
    throw new Error('useSecureFiles must be used within SecureFilesProvider');
  }
  return context;
}

export function SecureFilesProvider({ children }) {
  const { user } = useAuth();
  const { verifyPin, checkUnlockStatus, pinSettings } = usePin();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filters, setFilters] = useState({
    category: '',
    pinProtectedOnly: false,
    searchTerm: ''
  });

  // 파일 목록 로드
  const loadFiles = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    
    try {
      const filesList = await getSecureFiles(filters);
      setFiles(filesList);
    } catch (err) {
      console.error('Error loading files:', err);
      setError(err.message || '파일 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  // 사용자 변경 시 파일 목록 로드
  useEffect(() => {
    if (user) {
      loadFiles();
    } else {
      setFiles([]);
    }
  }, [user, loadFiles]);

  // 파일 업로드
  const uploadFile = async (file, metadata = {}) => {
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      // 업로드 진행률 시뮬레이션 (실제로는 Firebase SDK에서 제공하는 진행률 사용)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const uploadedFile = await uploadSecureFile(file, metadata);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // 파일 목록에 추가
      setFiles(prev => [uploadedFile, ...prev]);
      
      setTimeout(() => setUploadProgress(0), 1000);
      
      return uploadedFile;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err.message || '파일 업로드에 실패했습니다.');
      setUploadProgress(0);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 파일 다운로드
  const downloadFile = async (fileId, pin = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // 마스터 PIN이 설정되어 있고 잠금 해제 상태면 PIN 없이 다운로드
      let effectivePin = pin;
      if (!pin && pinSettings.hasMasterPin && checkUnlockStatus()) {
        // 마스터 PIN으로 이미 잠금 해제된 상태
        effectivePin = null;
      }
      
      const { blob, fileName, mimeType } = await downloadSecureFile(fileId, effectivePin);
      
      // 다운로드 링크 생성 및 클릭
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return true;
    } catch (err) {
      // PIN 검증 실패는 사용자 입력 오류이므로 에러 로그 생략
      if (!err.message?.includes('PIN')) {
        console.error('Error downloading file:', err);
      }
      setError(err.message || '파일 다운로드에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 파일 미리보기 (이미지, PDF 등)
  const previewFile = async (fileId, pin = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const { blob, fileName, mimeType } = await downloadSecureFile(fileId, pin);
      
      // 미리보기 가능한 파일 타입 확인
      const previewableTypes = ['image/', 'application/pdf', 'text/'];
      const isPreviewable = previewableTypes.some(type => mimeType.startsWith(type));
      
      if (!isPreviewable) {
        throw new Error('이 파일은 미리보기를 지원하지 않습니다.');
      }
      
      const url = URL.createObjectURL(blob);
      
      return {
        url,
        fileName,
        mimeType,
        cleanup: () => URL.revokeObjectURL(url)
      };
    } catch (err) {
      console.error('Error previewing file:', err);
      setError(err.message || '파일 미리보기에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 파일 메타데이터 업데이트 (보안 강화: PIN 보호 변경 시 검증)
  const updateFile = async (fileId, updates, pin = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // 현재 파일 정보 확인
      const currentFile = files.find(f => f.id === fileId);
      if (!currentFile) {
        throw new Error('파일을 찾을 수 없습니다.');
      }
      
      // PIN 보호 제거 시도 시 PIN 검증 필요
      if (currentFile.isPinProtected && 
          updates.isPinProtected === false && 
          pinSettings.hasMasterPin && 
          !checkUnlockStatus()) {
        if (!pin) {
          throw new Error('PIN 보호를 해제하려면 Master PIN이 필요합니다.');
        }
        const isValid = await verifyPin(pin);
        if (!isValid) {
          throw new Error('잘못된 PIN입니다.');
        }
      }
      
      await updateFileMetadata(fileId, updates);
      
      // 로컬 상태 업데이트
      setFiles(prev => prev.map(file => 
        file.id === fileId 
          ? { ...file, ...updates, updatedAt: new Date() }
          : file
      ));
      
      return true;
    } catch (err) {
      console.error('Error updating file:', err);
      setError(err.message || '파일 정보 업데이트에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 파일 삭제
  const deleteFile = async (fileId, pin = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // 마스터 PIN이 설정되어 있고 잠금 해제 상태면 PIN 없이 삭제
      let effectivePin = pin;
      if (!pin && pinSettings.hasMasterPin && checkUnlockStatus()) {
        // 마스터 PIN으로 이미 잠금 해제된 상태
        effectivePin = null;
      }
      
      await deleteSecureFile(fileId, effectivePin);
      
      // 로컬 상태에서 제거
      setFiles(prev => prev.filter(file => file.id !== fileId));
      
      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err.message || '파일 삭제에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 파일 공유 링크 생성
  const shareFile = async (fileId, expirationHours = 24, pin = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const shareData = await createShareLink(fileId, expirationHours, pin);
      
      // 로컬 상태 업데이트
      setFiles(prev => prev.map(file => 
        file.id === fileId 
          ? { ...file, isShared: true, shareExpiresAt: shareData.expiresAt }
          : file
      ));
      
      return shareData;
    } catch (err) {
      console.error('Error sharing file:', err);
      setError(err.message || '파일 공유 링크 생성에 실패했습니다.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // 필터 업데이트
  const updateFilters = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  // 필터 초기화
  const resetFilters = () => {
    setFilters({
      category: '',
      pinProtectedOnly: false,
      searchTerm: ''
    });
  };

  // 파일 검색
  const searchFiles = useCallback((searchTerm) => {
    if (!searchTerm) return files;
    
    const term = searchTerm.toLowerCase();
    return files.filter(file => 
      file.originalName?.toLowerCase().includes(term) ||
      file.description?.toLowerCase().includes(term) ||
      file.tags?.some(tag => tag.toLowerCase().includes(term))
    );
  }, [files]);

  // 통계 정보
  const stats = {
    total: files.length,
    pinProtected: files.filter(f => f.isPinProtected).length,
    shared: files.filter(f => f.isShared).length,
    totalSize: files.reduce((acc, f) => acc + (f.fileSize || 0), 0),
    categories: [...new Set(files.map(f => f.category))].filter(Boolean)
  };

  const value = {
    files,
    loading,
    error,
    uploadProgress,
    filters,
    stats,
    loadFiles,
    uploadFile,
    downloadFile,
    previewFile,
    updateFile,
    deleteFile,
    shareFile,
    updateFilters,
    resetFilters,
    searchFiles
  };

  return (
    <SecureFilesContext.Provider value={value}>
      {children}
    </SecureFilesContext.Provider>
  );
}

export default SecureFilesContext;